import React, { useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { func, number, string } from 'prop-types';
import styled from 'styled-components';
import _cloneDeep from 'lodash/cloneDeep';
import _trim from 'lodash/trim';

import { get } from 'ravenjs/utils/lodash';
import { addSchemaValue, calculateSort } from 'ravenjs/utils';

import { actions as adminActions } from 'modules/admin';
import { actions as uiActions } from 'modules/ui';
import { selectors as userSelectors } from 'modules/user';
import USERS from 'constants/users';
import { DetailsViewList } from 'ravenjs/components';
import { Button } from 'ravenjs/lib';
import AccountSwitcherListSchema from './AccountSwitcherListSchema.json';
import PrimaryAccountNameCell from './PrimaryAccountNameCell';

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 600px;
    padding: 0;
`;

const TableSection = styled.div`
    margin-bottom: 8px;

    /* Style disabled radio buttons for primary account with #808285 color */
    [disabled] span,
    [aria-disabled='true'] span {
        border-color: #808285 !important;
    }
    [disabled] span span,
    [aria-disabled='true'] span span {
        background-color: #808285 !important;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 40px;
`;

const ButtonStyled = styled(Button)`
    width: 200px;
    height: 48px;
    font-size: 16px;
    font-family: 'ABC Favorit Extended';
`;

const HeaderSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
`;

const Title = styled.h2`
    font-family: 'Favorit';
    font-weight: 500;
    font-size: 18px;
    line-height: 120%;
    color: #282828;
    margin: 0;
`;

const Subheading = styled.div`
    font-family: 'Roboto';
    font-weight: 400;
    font-size: 16px;
    line-height: 150%;
    color: #282828;
`;

const SubheadingLabel = styled.span`
    font-weight: 700;
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid #e0e0e0;
    margin: 0;
    width: 100%;
`;

export const UnwrappedAccountSwitcherList = ({
    fetchUserCompanyAssignments,
    userId,
    isLoading,
    currentPartnerId,
    currentPartnerName,
    onCancel,
    onConfirm,
}) => {
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);

    const defaultConfigRef = useRef({
        page: get(AccountSwitcherListSchema, 'pagination.startPage', 1),
        rowsPerPage: get(AccountSwitcherListSchema, 'pagination.rowsPerPage', 20),
        order: get(AccountSwitcherListSchema, 'sorting.default.order', 'asc'),
        orderBy: get(AccountSwitcherListSchema, 'sorting.default.orderBy', 'partnerName'),
        searchSpec: null,
    });

    const listSchema = useMemo(() => {
        const clonedSchema = _cloneDeep(AccountSwitcherListSchema);
        addSchemaValue(clonedSchema, 'list.cellTypes', {
            PrimaryAccountNameCell,
        });
        if (currentPartnerId) {
            addSchemaValue(clonedSchema, 'selected.items', [currentPartnerId]);
        }
        return clonedSchema;
    }, [currentPartnerId]);

    const fetchAssignedPartners = useCallback(
        async (config = {}) => {
            const hasConfig = config && Object.keys(config).length > 0;
            const effectiveConfig = hasConfig
                ? { ...defaultConfigRef.current, ...config }
                : defaultConfigRef.current;

            const { page, rowsPerPage, searchSpec, order, orderBy } = effectiveConfig;
            defaultConfigRef.current = effectiveConfig;

            const offset = rowsPerPage > 0 ? Math.max((page - 1) * rowsPerPage, 0) : 0;
            const sort = calculateSort(order, orderBy);

            const params = {
                limit: rowsPerPage,
                offset,
            };

            if (sort) {
                params.sort = sort;
            }

            const trimmedSearch = _trim(searchSpec || '');
            if (trimmedSearch) {
                params.searchSpec = trimmedSearch;
            }

            isLoading(true);
            try {
                const response = await fetchUserCompanyAssignments({
                    userId,
                    params,
                });
                const totalRecords = get(response, 'totalRecords', 0);
                return {
                    data: get(response, 'assignedCompanies', []),
                    totalRows: totalRecords,
                };
            } catch (e) {
                return { data: [], totalRows: 0 };
            } finally {
                isLoading(false);
            }
        },
        [fetchUserCompanyAssignments, isLoading, userId]
    );
    const isRadioRowSelected = useCallback(({ row, selected }) => {
        const rowId = get(row, 'companyId');
        return Array.isArray(selected) && selected.includes(rowId);
    }, []);

    const onRadioRowSelect = useCallback(rowId => {
        setSelectedCompanyId(rowId);
    }, []);

    const isPrimaryRowDisabled = useCallback(
        ({ row }) => {
            if (!row) return false;
            const rowCompanyId = get(row, 'companyId');
            return rowCompanyId === currentPartnerId;
        },
        [currentPartnerId]
    );

    const isConfirmDisabled = !selectedCompanyId || selectedCompanyId === currentPartnerId;

    const handleConfirm = useCallback(() => {
        if (selectedCompanyId) {
            onConfirm(selectedCompanyId);
        }
    }, [selectedCompanyId, onConfirm]);

    const handleOnChange = useCallback(() => {}, []);

    return (
        <ModalContainer>
            <HeaderSection>
                <Title>{USERS.SWITCH_ACCOUNT_MODAL.SWITCH_ACCOUNT}</Title>
                <Subheading>
                    <SubheadingLabel>
                        {USERS.SWITCH_ACCOUNT_MODAL.CURRENTLY_MANAGING}
                    </SubheadingLabel>{' '}
                    {currentPartnerName}
                </Subheading>
            </HeaderSection>
            <Divider />
            <TableSection>
                <DetailsViewList
                    schema={listSchema}
                    actions={{
                        onChange: handleOnChange,
                        fetchAssignedPartners,
                        isRadioRowSelected,
                        onRadioRowSelect,
                        isPrimaryRowDisabled,
                    }}
                />
            </TableSection>
            <ButtonContainer>
                <ButtonStyled color="secondary" onClick={onCancel}>
                    {USERS.SWITCH_ACCOUNT_MODAL.BUTTON.CANCEL}
                </ButtonStyled>
                <ButtonStyled color="primary" disabled={isConfirmDisabled} onClick={handleConfirm}>
                    {USERS.SWITCH_ACCOUNT_MODAL.BUTTON.CONFIRM}
                </ButtonStyled>
            </ButtonContainer>
        </ModalContainer>
    );
};

UnwrappedAccountSwitcherList.propTypes = {
    fetchUserCompanyAssignments: func.isRequired,
    onCancel: func.isRequired,
    onConfirm: func.isRequired,
    userId: number,
    isLoading: func.isRequired,
    currentPartnerId: number,
    currentPartnerName: string,
};

UnwrappedAccountSwitcherList.defaultProps = {
    userId: null,
    currentPartnerId: null,
    currentPartnerName: '',
};

const mapStateToProps = createStructuredSelector({
    userId: userSelectors.getUserId,
    currentPartnerId: userSelectors.getPartnerId,
});

const mapDispatchToProps = {
    fetchUserCompanyAssignments: adminActions.fetchUserCompanyAssignments.trigger,
    closeModal: uiActions.closeModal,
    isLoading: uiActions.isLoading,
};

export default connect(mapStateToProps, mapDispatchToProps)(UnwrappedAccountSwitcherList);
